import { __decorate, __metadata, __param } from "tslib";
import _ from 'lodash';
import { inject, injectable } from 'inversify';
import { BehaviorSubject } from 'rxjs';
import { UserCenterService } from '@patsnap-utils/biz';
import PassportLogin from '@patsnap-utils/passport/src/Login';
import initialize, { handleLogout } from '@patsnap-utils/passport';
import { passportInterceptManager } from './auth/auth';
import { Symbols } from '@/symbols';
import { currentClientUUID } from '@patsnap/uniform-setting';
import { ACCOUNT_URL } from '@/config/envConfig';
export function defineRedirectUri() {
    return encodeURIComponent(location.hash.replace('#', ''));
}
export const X_PATSNAP_FROM = 'w-backoffice';
class PreparePassport {
    constructor(config) {
        this.config = config;
        this.inited = false;
    }
    async prepare(autoChecksession) {
        const { baseAuthServer, psAxios, navgationService, } = this.config;
        const initializeConfig = {
            baseAuthServer,
            autoRun: autoChecksession,
            clientId: process.env.VUE_APP_PASSPORT_CLIENT_ID,
            clientName: process.env.VUE_APP_PASSPORT_CLIENT_NAME,
            redirectToLogin(error) {
                console.warn('Check Session Error, Will Redirect To Login Page!');
                const errorCode = _.get(error, 'errcode', '39999');
                navgationService.goToLogin(errorCode);
            },
            detectResponseAsUnauthorized(error) {
                const url = _.get(error, 'response.config.url');
                if (_.includes(url, '/backoffice/tool/postman')) {
                    return false;
                }
                return _.get(error, 'response.status') === 401;
            },
        };
        /* 清除 csrfTokenInterceptor */
        psAxios.interceptors.request.eject(psAxios.psInterceptors.csrfTokenInterceptor);
        psAxios.defaults.headers['X-Patsnap-From'] = X_PATSNAP_FROM;
        psAxios.defaults.headers['X-Device-ID'] = currentClientUUID();
        const passport = await initialize(initializeConfig);
        this.inited = true;
        return passport;
    }
}
/**
 * 角色等级排序配置
 */
export const rolesLevelOrderConfig = [
    {
        id: 'backoffice-developer_admin',
        name_cn: '超级管理员',
        name_us: 'Super Admin',
    },
    {
        id: 'id_backoffice_admin',
        name_cn: '管理员',
        name_us: 'Admin',
    },
    {
        id: 'internal_user_administrator',
        name_cn: '内部用户管理员',
        name_us: 'Internal user administrator',
    },
    {
        id: 'id_backoffice_bo_developer',
        name_cn: '高级开发',
        name_us: 'BO Developer',
    },
    {
        id: 'id_backoffice_pm',
        name_cn: '产品经理',
        name_us: 'PM',
    },
    {
        id: 'id_backoffice_cs',
        name_cn: '客服',
        name_us: 'CS',
    },
    {
        id: 'id_backoffice_sales',
        name_cn: '销售',
        name_us: 'Sales',
    },
    {
        id: 'id_backoffice_am_ka',
        name_cn: '大客户销售',
        name_us: 'AM_KA',
    },
    {
        id: 'id_backoffice_developer',
        name_cn: '普通开发',
        name_us: 'Developer',
    }
];
let AuthService = class AuthService {
    constructor(passportPublicUrls, dataResourceService, accountService, router, psAxios, i18n, navgationService) {
        this.passportPublicUrls = passportPublicUrls;
        this.dataResourceService = dataResourceService;
        this.accountService = accountService;
        this.router = router;
        this.psAxios = psAxios;
        this.i18n = i18n;
        this.navgationService = navgationService;
        this.hasLogin = false;
        this.currentUser = null;
        this.currentUser$ = new BehaviorSubject(null);
        this.currentUserDataResource$ = new BehaviorSubject([]);
        this.getPassportToken = () => {
            return this.preparePassport.prepare(true).then(passport => {
                return passport.getAccessToken();
            });
        };
        this.preparePassport = new PreparePassport({
            psAxios,
            baseAuthServer: this.passportPublicUrls.base,
            navgationService: this.navgationService,
        });
    }
    get IsUserLogin() {
        return this.hasLogin && this.currentUser;
    }
    /**
     *  当前登录用户 是否是销售角色
     */
    get IsOnlyViewFlag() {
        return this.currentUser && this.currentUser.only_view_flag;
    }
    get CurrentUserRole() {
        if (!this.currentUser)
            return null;
        const roleData = _.find(rolesLevelOrderConfig, roleConfig => {
            const item = _.find(this.currentUser.role_ids, id => {
                if (roleConfig.id === id) {
                    return true;
                }
            });
            if (item) {
                return true;
            }
        });
        return roleData;
    }
    /**
     * 内部用户管理员
     */
    get IsInternalUserAdministrator() {
        return this.CurrentUserRole && this.CurrentUserRole.id === 'internal_user_administrator';
    }
    /**
     * GlobalView 独权
     */
    get IsGlobalViewRole() {
        if (!this.currentUser)
            return null;
        const item = _.find(this.currentUser.role_ids, id => {
            if (id === 'id_backoffice_global_view') {
                return true;
            }
        });
        return !!item;
    }
    /**
     * 管理员
     */
    get IsAdmin() {
        return this.CurrentUserRole && this.CurrentUserRole.id === 'id_backoffice_admin';
    }
    /**
     * 超级管理员
     */
    get IsSuperAdmin() {
        if (!this.currentUser)
            return null;
        const item = _.find(this.currentUser.role_ids, id => {
            if (id === 'backoffice-developer_admin') {
                return true;
            }
        });
        return !!item;
    }
    /**
     * Cs
     */
    get IsCs() {
        if (!this.currentUser)
            return null;
        const item = _.find(this.currentUser.role_ids, id => {
            if (id === 'id_backoffice_cs') {
                return true;
            }
        });
        return !!item;
    }
    /**
     * Sales
     */
    get IsSales() {
        if (!this.currentUser)
            return null;
        const item = _.find(this.currentUser.role_ids, id => {
            if (id === 'id_backoffice_sales' || id === 'id_backoffice_am_ka') {
                return true;
            }
        });
        return !!item;
    }
    get canEditAccountManager() {
        if (!this.currentUser)
            return null;
        const item = _.find(this.currentUser.role_ids, id => {
            if (id === 'id_backoffice_edit_account_owner') {
                return true;
            }
        });
        return !!item;
    }
    get trialLimitNum() {
        return this.IsSales ? 5 : 100;
    }
    get assignLimitNum() {
        return this.IsSales ? 5 : 1000;
    }
    get CurrentUserRoleName() {
        if (this.CurrentUserRole) {
            return this.i18n.locale.toLocaleLowerCase() === 'cn' ? this.CurrentUserRole.name_cn : this.CurrentUserRole.name_us;
        }
        return null;
    }
    get OldLoginUrl() {
        return UserCenterService.createLoginHref(false, {
            redirect_uri: `${location.origin}${this.router.resolve({ name: 'companies' }).href}`,
        });
    }
    get ResetPasswordUrl() {
        return this.passportPublicUrls.forget;
    }
    enablePassport() {
        return this.preparePassport.prepare(false);
    }
    /**
     * 构建MFA跳转URL
     */
    buildMFARedirectUrl() {
        // 这里需要根据实际的MFA页面URL来构建
        // 可以从配置中获取或者使用固定的URL模式
        const baseUrl = ACCOUNT_URL;
        const redirectUrl = encodeURIComponent(window.location.href);
        return `${baseUrl}/user-settings/#/security-settings?redirect=${redirectUrl}`;
    }
    getMfaCk() {
        return this.accountService.getMfaCk().then(res => {
            return true;
        }).catch(err => {
            if (err.response?.status === 403) {
                const errorData = err.response.data;
                if (errorData?.numeric_error_code === 32004) {
                    // 构建MFA跳转URL，这里需要根据实际情况调整
                    const mfaRedirectUrl = this.buildMFARedirectUrl();
                    window.location.href = mfaRedirectUrl;
                    return false;
                }
            }
            return true;
        });
    }
    async enableUser() {
        if (this.IsUserLogin) {
            return;
        }
        try {
            await this.preparePassport.prepare(true);
            passportInterceptManager.inject();
            this.hasLogin = true;
            // const mfaCk = await this.getMfaCk()
            // if (!mfaCk) {
            //     return
            // }
            const accountInfo = await this.accountService.getCurrentUser();
            this.updateUserInfo(accountInfo);
            // 同时获取当前用户的数据资源
            const dataResource = await this.dataResourceService.getCurrentUserDataResource();
            this.updateDataResource(dataResource);
        }
        catch (err) {
            this.hasLogin = false;
            throw err;
        }
    }
    async preLogin(params) {
        const { username, password, } = params;
        return this.accountService.preLogin({
            username,
            password,
        });
    }
    login(params) {
        const { email, password, rememberMe, mfaCode, } = params;
        const login = new PassportLogin();
        return login.submitLoginForm(email, password, {
            rememberMe,
            ...(mfaCode
                ? {
                    mfa_code: mfaCode,
                    mfa_type: 'TOTP',
                }
                : {}),
        });
    }
    logout() {
        this.hasLogin = false;
        this.updateUserInfo(null);
        this.updateDataResource([]);
        return handleLogout().then(() => {
            this.navgationService.goToLogin();
        });
    }
    getIdentifyToken() {
        return this.accountService.getIdentifyToken();
    }
    updateUserInfo(info) {
        this.currentUser = info;
        this.currentUser$.next(info);
    }
    updateDataResource(data) {
        this.currentUserDataResource$.next(data);
    }
};
AuthService = __decorate([
    injectable(),
    __param(0, inject(Symbols.PassportPublicUrls)),
    __param(1, inject(Symbols.DataResourceApi)),
    __param(2, inject(Symbols.AccountApi)),
    __param(3, inject(Symbols.Router)),
    __param(4, inject(Symbols.PsAxios)),
    __param(5, inject(Symbols.I18n)),
    __param(6, inject(Symbols.NavgationService)),
    __metadata("design:paramtypes", [Object, Object, Object, Object, Object, Object, Object])
], AuthService);
export { AuthService };
